/**
 * @fileoverview TanStack Query hooks for Messaging Conversations
 *
 * Conversations are threads of messages with external contacts.
 * They belong to a channel and can be linked to CRM contacts.
 *
 * @module lib/query/hooks/useMessagingConversationsQuery
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { queryKeys, DEALS_VIEW_KEY } from '../queryKeys';
import { getClient } from '@/lib/supabase/client';
import { useAuth } from '@/context/AuthContext';
import type {
  MessagingConversation,
  ConversationView,
  ConversationFilters,
  ConversationStatus,
  UpdateConversationInput,
} from '@/lib/messaging/types';
import {
  transformConversation,
  isWindowExpired,
  getWindowMinutesRemaining,
} from '@/lib/messaging/types';

// =============================================================================
// CONSTANTS
// =============================================================================

const DEFAULT_PAGE_SIZE = 50;

// =============================================================================
// QUERY HOOKS
// =============================================================================

/**
 * Fetch conversations with filters (inbox view).
 * Returns ConversationView with denormalized data.
 */
export function useMessagingConversations(filters?: ConversationFilters) {
  const { user, loading: authLoading } = useAuth();

  return useQuery({
    queryKey: queryKeys.messagingConversations.filtered(filters),
    queryFn: async ({ signal }): Promise<ConversationView[]> => {
      const supabase = getClient();

      let query = supabase
        .from('messaging_conversations')
        .select(`
          *,
          channel:messaging_channels!channel_id (
            id,
            channel_type,
            name,
            provider
          ),
          assigned_user:profiles!assigned_user_id (
            id,
            name,
            avatar
          )
        `)
        .abortSignal(signal);

      // Apply filters
      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }
      if (filters?.channelId) {
        query = query.eq('channel_id', filters.channelId);
      }
      if (filters?.businessUnitId) {
        query = query.eq('business_unit_id', filters.businessUnitId);
      }
      if (filters?.assignedUserId) {
        if (filters.assignedUserId === 'unassigned') {
          query = query.is('assigned_user_id', null);
        } else {
          query = query.eq('assigned_user_id', filters.assignedUserId);
        }
      }
      if (filters?.hasUnread) {
        query = query.gt('unread_count', 0);
      }
      if (filters?.search) {
        query = query.or(`external_contact_name.ilike.%${filters.search}%,last_message_preview.ilike.%${filters.search}%`);
      }

      // Apply sorting
      const sortBy = filters?.sortBy || 'lastMessageAt';
      const sortOrder = filters?.sortOrder || 'desc';
      const sortColumn = sortBy === 'lastMessageAt' ? 'last_message_at' :
                        sortBy === 'createdAt' ? 'created_at' : 'unread_count';
      query = query.order(sortColumn, { ascending: sortOrder === 'asc', nullsFirst: false });

      const { data, error } = await query.limit(100);

      if (error) throw error;

      // Busca contatos SEPARADAMENTE (mesmo padrão do useDealsQuery).
      // O JOIN direto na query estava retornando classification/stage vazio às vezes.
      const contactIds = [...new Set(
        (data || []).map((r: any) => r.contact_id).filter(Boolean)
      )] as string[];

      const contactMap = new Map<string, any>();
      if (contactIds.length > 0) {
        const { data: contactsData } = await supabase
          .from('contacts')
          .select('id, name, email, phone, ai_paused, classification, stage')
          .in('id', contactIds);
        for (const c of contactsData || []) {
          contactMap.set(c.id, c);
        }
      }

      // Busca o deal ativo mais recente por contato (para badge baseada em board+etapa).
      const dealMap = new Map<string, { boardIsSystemInbox: boolean; boardIsSystemContacts: boolean; stageLabel: string | null; stageIsSystem: boolean }>();
      if (contactIds.length > 0) {
        const { data: dealsData } = await supabase
          .from('deals')
          .select('contact_id, updated_at, board:boards!board_id(is_system_inbox, is_system_contatos), stage:board_stages!stage_id(label, is_system)')
          .in('contact_id', contactIds)
          .is('deleted_at', null)
          .eq('is_won', false)
          .eq('is_lost', false)
          .order('updated_at', { ascending: false });
        for (const d of dealsData || []) {
          const cid = (d as any).contact_id as string;
          if (!cid || dealMap.has(cid)) continue;
          const board = (d as any).board as { is_system_inbox?: boolean; is_system_contatos?: boolean } | null;
          const stage = (d as any).stage as { label?: string; is_system?: boolean } | null;
          dealMap.set(cid, {
            boardIsSystemInbox: !!board?.is_system_inbox,
            boardIsSystemContacts: !!board?.is_system_contatos,
            stageLabel: stage?.label ?? null,
            stageIsSystem: !!stage?.is_system,
          });
        }
      }

      // Transform to ConversationView
      return (data || []).map((row) => {
        const base = transformConversation(row);
        const channel = row.channel as { id?: string; channel_type?: string; name?: string; provider?: string } | null;
        const contact = row.contact_id ? contactMap.get(row.contact_id) : null;
        const assignedUser = row.assigned_user as { id?: string; name?: string; avatar?: string } | null;
        const dealCtx = row.contact_id ? dealMap.get(row.contact_id) : null;

        return {
          ...base,
          channelType: channel?.channel_type as ConversationView['channelType'],
          channelName: channel?.name || '',
          channelProvider: channel?.provider,
          contactName: contact?.name,
          contactEmail: contact?.email,
          contactPhone: contact?.phone,
          contactAiPaused: contact?.ai_paused ?? false,
          contactClassification: contact?.classification,
          contactStage: contact?.stage,
          dealBoardIsSystemInbox: dealCtx?.boardIsSystemInbox,
          dealBoardIsSystemContacts: dealCtx?.boardIsSystemContacts,
          dealStageLabel: dealCtx?.stageLabel ?? undefined,
          dealStageIsSystem: dealCtx?.stageIsSystem,
          assignedUserName: assignedUser?.name,
          assignedUserAvatar: assignedUser?.avatar,
          isWindowExpired: isWindowExpired(base, channel?.provider),
          windowMinutesRemaining: getWindowMinutesRemaining(base),
        };
      });
    },
    staleTime: 30 * 1000, // 30 seconds (conversations change frequently)
    enabled: !authLoading && !!user,
  });
}

/**
 * Fetch conversations for a specific channel.
 */
export function useConversationsByChannel(channelId: string | undefined) {
  const { user, loading: authLoading } = useAuth();

  return useQuery({
    queryKey: queryKeys.messagingConversations.byChannel(channelId || ''),
    queryFn: async (): Promise<MessagingConversation[]> => {
      if (!channelId) return [];

      const supabase = getClient();

      const { data, error } = await supabase
        .from('messaging_conversations')
        .select('*')
        .eq('channel_id', channelId)
        .order('last_message_at', { ascending: false, nullsFirst: false });

      if (error) throw error;
      return (data || []).map(transformConversation);
    },
    staleTime: 30 * 1000,
    enabled: !authLoading && !!user && !!channelId,
  });
}

/**
 * Fetch conversations for a specific contact.
 */
export function useConversationsByContact(contactId: string | undefined) {
  const { user, loading: authLoading } = useAuth();

  return useQuery({
    queryKey: queryKeys.messagingConversations.byContact(contactId || ''),
    queryFn: async (): Promise<MessagingConversation[]> => {
      if (!contactId) return [];

      const supabase = getClient();

      const { data, error } = await supabase
        .from('messaging_conversations')
        .select('*')
        .eq('contact_id', contactId)
        .order('last_message_at', { ascending: false, nullsFirst: false });

      if (error) throw error;
      return (data || []).map(transformConversation);
    },
    staleTime: 30 * 1000,
    enabled: !authLoading && !!user && !!contactId,
  });
}

/**
 * Fetch a single conversation by ID with full details.
 */
export function useMessagingConversation(conversationId: string | undefined) {
  const { user, loading: authLoading } = useAuth();

  return useQuery({
    queryKey: queryKeys.messagingConversations.detail(conversationId || ''),
    queryFn: async (): Promise<ConversationView | null> => {
      if (!conversationId) return null;

      const supabase = getClient();

      const { data, error } = await supabase
        .from('messaging_conversations')
        .select(`
          *,
          channel:messaging_channels!channel_id (
            id,
            channel_type,
            name,
            provider
          ),
          contact:contacts!contact_id (
            id,
            name,
            email,
            phone,
            avatar,
            ai_paused,
            classification
          ),
          assigned_user:profiles!assigned_user_id (
            id,
            name,
            avatar
          )
        `)
        .eq('id', conversationId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null;
        throw error;
      }

      const base = transformConversation(data);
      const channel = data.channel as { id?: string; channel_type?: string; name?: string; provider?: string } | null;
      const contact = data.contact as { id?: string; name?: string; email?: string; phone?: string; ai_paused?: boolean; classification?: string } | null;
      const assignedUser = data.assigned_user as { id?: string; name?: string; avatar?: string } | null;

      return {
        ...base,
        channelType: channel?.channel_type as ConversationView['channelType'],
        channelName: channel?.name || '',
        channelProvider: channel?.provider,
        contactName: contact?.name,
        contactEmail: contact?.email,
        contactPhone: contact?.phone,
        contactAiPaused: contact?.ai_paused ?? false,
        contactClassification: contact?.classification,
        assignedUserName: assignedUser?.name,
        assignedUserAvatar: assignedUser?.avatar,
        isWindowExpired: isWindowExpired(base, channel?.provider),
        windowMinutesRemaining: getWindowMinutesRemaining(base),
      };
    },
    staleTime: 30 * 1000,
    enabled: !authLoading && !!user && !!conversationId,
  });
}

/**
 * Fetch unread conversation count.
 */
export function useUnreadConversationCount() {
  const { user, loading: authLoading } = useAuth();

  return useQuery({
    queryKey: queryKeys.messagingConversations.unreadCount(),
    queryFn: async (): Promise<number> => {
      const supabase = getClient();

      const { data, error } = await supabase.rpc('get_messaging_unread_count');

      if (error) throw error;
      return data ?? 0;
    },
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000, // Refetch every minute
    enabled: !authLoading && !!user,
  });
}

// =============================================================================
// MUTATION HOOKS
// =============================================================================

/**
 * Update a conversation (status, priority, assignment).
 */
export function useUpdateConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      conversationId,
      input,
    }: {
      conversationId: string;
      input: UpdateConversationInput;
    }): Promise<MessagingConversation> => {
      const supabase = getClient();

      const updateData: Record<string, unknown> = {
        updated_at: new Date().toISOString(),
      };

      if (input.status !== undefined) {
        updateData.status = input.status;
      }
      if (input.priority !== undefined) {
        updateData.priority = input.priority;
      }
      if (input.assignedUserId !== undefined) {
        updateData.assigned_user_id = input.assignedUserId;
        updateData.assigned_at = input.assignedUserId
          ? new Date().toISOString()
          : null;
      }

      const { data, error } = await supabase
        .from('messaging_conversations')
        .update(updateData)
        .eq('id', conversationId)
        .select()
        .single();

      if (error) throw error;

      return transformConversation(data);
    },
    onSettled: (conversation) => {
      // Invalidate filtered queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.messagingConversations.all,
      });
      // Update detail cache
      if (conversation) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.messagingConversations.detail(conversation.id),
        });
      }
    },
  });
}

/**
 * Mark a conversation as read (reset unread count).
 */
export function useMarkConversationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (conversationId: string): Promise<void> => {
      // Use the API route so the WhatsApp read receipt (uazapi) is also fired,
      // not just the local unread_count reset. The endpoint is best-effort —
      // if uazapi fails, the DB still gets updated.
      const res = await fetch(`/api/messaging/conversations/${conversationId}/mark-read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        // Fall back to the local-only RPC so unread state still clears
        const supabase = getClient();
        const { error } = await supabase.rpc('mark_conversation_read', {
          p_conversation_id: conversationId,
        });
        if (error) throw error;
      }
    },
    onSettled: (_, _err, conversationId) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.messagingConversations.detail(conversationId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.messagingConversations.unreadCount(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.messagingConversations.all,
      });
      // Atualiza o board para remover o badge AGUARDANDO
      queryClient.invalidateQueries({ queryKey: DEALS_VIEW_KEY });
    },
  });
}

/**
 * Resolve (close) a conversation.
 */
export function useResolveConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId: string): Promise<MessagingConversation> => {
      const supabase = getClient();
      const { data, error } = await supabase
        .from('messaging_conversations')
        .update({ status: 'resolved' as ConversationStatus, updated_at: new Date().toISOString() })
        .eq('id', conversationId)
        .select()
        .single();
      if (error) throw error;
      return transformConversation(data);
    },
    onSettled: (conversation) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.messagingConversations.all });
      if (conversation) {
        queryClient.invalidateQueries({ queryKey: queryKeys.messagingConversations.detail(conversation.id) });
      }
    },
  });
}

/**
 * Reopen a conversation.
 */
export function useReopenConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId: string): Promise<MessagingConversation> => {
      const supabase = getClient();
      const { data, error } = await supabase
        .from('messaging_conversations')
        .update({ status: 'open' as ConversationStatus, updated_at: new Date().toISOString() })
        .eq('id', conversationId)
        .select()
        .single();
      if (error) throw error;
      return transformConversation(data);
    },
    onSettled: (conversation) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.messagingConversations.all });
      if (conversation) {
        queryClient.invalidateQueries({ queryKey: queryKeys.messagingConversations.detail(conversation.id) });
      }
    },
  });
}

/**
 * Assign a conversation to a user.
 */
export function useAssignConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      conversationId,
      userId,
    }: {
      conversationId: string;
      userId: string | null;
    }): Promise<MessagingConversation> => {
      const supabase = getClient();
      const { data, error } = await supabase
        .from('messaging_conversations')
        .update({
          assigned_user_id: userId,
          assigned_at: userId ? new Date().toISOString() : null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', conversationId)
        .select()
        .single();
      if (error) throw error;
      return transformConversation(data);
    },
    onSettled: (conversation) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.messagingConversations.all });
      if (conversation) {
        queryClient.invalidateQueries({ queryKey: queryKeys.messagingConversations.detail(conversation.id) });
      }
    },
  });
}

/**
 * Toggle AI pause on a conversation (metadata-level, no linked contact required).
 */
export function useToggleConversationAiPause() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      conversationId,
      paused,
      currentMetadata,
    }: {
      conversationId: string;
      paused: boolean;
      currentMetadata: Record<string, unknown>;
    }): Promise<void> => {
      const supabase = getClient();

      const { error } = await supabase
        .from('messaging_conversations')
        .update({
          metadata: { ...currentMetadata, ai_paused: paused },
          updated_at: new Date().toISOString(),
        })
        .eq('id', conversationId);

      if (error) throw error;
    },
    onSettled: (_, _err, { conversationId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.messagingConversations.detail(conversationId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.messagingConversations.all,
      });
    },
  });
}

/**
 * Link a conversation to a CRM contact.
 */
export function useLinkConversationToContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      conversationId,
      contactId,
    }: {
      conversationId: string;
      contactId: string | null;
    }): Promise<void> => {
      const supabase = getClient();

      const { error } = await supabase
        .from('messaging_conversations')
        .update({
          contact_id: contactId,
          updated_at: new Date().toISOString(),
        })
        .eq('id', conversationId);

      if (error) throw error;
    },
    onSettled: (_, _err, { conversationId, contactId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.messagingConversations.detail(conversationId),
      });
      if (contactId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.messagingConversations.byContact(contactId),
        });
      }
    },
  });
}
